import React, { useState, useRef } from "react";
import { Wordmark, SMLogo } from "@/components/UIBits";
import { T } from "@/lib/copy";
import { X, Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toBlob, getFontEmbedCSS } from "html-to-image"; // B57: getFontEmbedCSS forces Saira Condensed embed in exported PNG.

const SPOILER_TIER = { "6": "gold", "4": "gold", "2": "warm", "1": "warm", "0": "dim", "W": "cold" };

// Wordle-style spoiler-free grid — colour ONLY, no numerals. The recipient sees
// the SHAPE of the innings (blaze / warm / dim / cold beats) without learning
// which ball was what, preserving the "pulled to play" mechanic.
function SpoilerGrid({ grid = [] }) {
  // B49: reveal per-ball outcomes. B51: at 6 tiles, compress size+gap inline to guarantee single-row Wordle-shape.
  const full = grid.length >= 6;
  const tileStyle = full ? { width: 38, height: 38, fontSize: 15 } : undefined;
  return (
    <div className={"flex items-center justify-center " + (full ? "gap-1.5" : "gap-2")} style={{ flexWrap: "nowrap" }} data-testid="share-card-grid">
      {grid.map((o, i) => (
        <div key={i} className={"tile " + (SPOILER_TIER[o] || "dim")} style={tileStyle}>{o === "0" ? "•" : o}</div>
      ))}
    </div>
  );
}

function fmtDate(d = new Date()) {
  const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export default function ShareModal({ open, onClose, summary, persona, lang, createShare }) {
  const t = T(lang);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const fontEmbedCssRef = useRef(null); // B57: cached embed CSS to avoid re-fetching per share.
  if (!open) return null;

  const playerName = (summary.player_name || "").trim();
  const overNo = summary.over_no;
  const streak = summary.streak || 0;
  const verdict = summary.share_verdict || shortVerdict(summary.close);

  function buildText(token) {
    // B56: score line removed — the OG card image already carries the score visually. Reduce caption noise.
    // Tranche G · Share the UNFURL card URL so WhatsApp/iMessage/Twitter render an OG preview.
    // Bug 2: shortened path /api/s/{token} → 302 redirects to the full /api/share/{token}/card unfurl.
    const url = `https://${shareDomain()}` + (token ? `/api/s/${token}` : "");
    return `SUPER OVER — A Sports Mantra game\n${lang === "hi" ? "Tum se better?" : "Think you'd have called it better?"}\n${url}`;
  }

  async function renderCardImage() {
    if (!cardRef.current) return null;
    try {
      // B57: wait for browser to finish font loading before serialising, and force-embed the fonts
      // so html-to-image doesn't fall back to sans-serif inside the exported PNG (which was wrapping
      // the "SUPER OVER" wordmark). Embed CSS is cached across re-shares.
      if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch (e) {} }
      if (!fontEmbedCssRef.current) {
        try { fontEmbedCssRef.current = await getFontEmbedCSS(cardRef.current); } catch (e) {}
      }
      const opts = { pixelRatio: 2.5, cacheBust: true, backgroundColor: "#070C18" };
      if (fontEmbedCssRef.current) opts.fontEmbedCSS = fontEmbedCssRef.current;
      const blob = await toBlob(cardRef.current, opts);
      return blob;
    } catch (e) {
      return null;
    }
  }

  async function getToken() {
    try { const r = await createShare(); return r && r.token; } catch (e) { return null; }
  }

  async function doShare() {
    setBusy(true);
    const token = await getToken();
    const text = buildText(token);
    const blob = await renderCardImage();
    setBusy(false);

    // Preferred: share the CARD IMAGE itself (the hero), with the line as caption.
    if (blob && navigator.canShare) {
      const file = new File([blob], "super-over.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text, title: "Super Over" }); return; } catch (e) { if (e && e.name === "AbortError") return; }
      }
    }
    // Fallback 1: text/link share if available
    if (navigator.share) {
      try { await navigator.share({ title: "Super Over", text }); return; } catch (e) { if (e && e.name === "AbortError") return; }
    }
    // Fallback 2: download the card + copy the line
    if (blob) { triggerDownload(blob); }
    try { await navigator.clipboard.writeText(text); } catch (e) {}
    toast.success(lang === "hi" ? "Card download ho gaya — group mein bhejo" : "Card saved — drop it in your group");
  }

  async function doDownload() {
    setBusy(true);
    const blob = await renderCardImage();
    setBusy(false);
    if (blob) { triggerDownload(blob); toast.success(lang === "hi" ? "Card save ho gaya" : "Card saved"); }
    else { toast(lang === "hi" ? "Card long-press karke screenshot lo" : "Long-press the card to screenshot it"); }
  }

  function triggerDownload(blob) {
    try {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "super-over.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) {}
  }

  // Identity strip pieces: NAME · OVER #N · DATE
  const idBits = [];
  if (playerName) idBits.push(playerName.toUpperCase());
  if (overNo) idBits.push(`OVER #${overNo}`);
  idBits.push(fmtDate());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,10,20,0.82)", backdropFilter: "blur(6px)" }} onClick={onClose} data-testid="share-modal">
      <div className="relative w-full" style={{ maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
        <button className="absolute top-2 right-2 btn-ghost z-10" onClick={onClose} data-testid="share-close" style={{ background: "rgba(10,15,25,0.55)", borderRadius: 999, padding: 6 }}><X size={20} /></button> {/* B60: reposition inside container so it stays visible on short mobile viewports (was -top-10, going off-screen behind Safari URL bar). */}

        {/* the share card (the HERO — exported to an image on share) */}
        <div ref={cardRef} className="relative overflow-hidden" data-testid="share-card-preview"
          style={{ borderRadius: 24, padding: 26, border: "1px solid var(--stroke-strong)",
            background: "radial-gradient(120% 60% at 50% -10%, rgba(232,132,44,0.18) 0%, rgba(10,20,40,0) 55%), linear-gradient(180deg, #0c182e, #070C18)" }}>
          <div className="grain" />
          <div className="relative flex flex-col items-center text-center gap-3">
            <div className="font-mono up" style={{ fontSize: 9.5, letterSpacing: "0.22em", color: "rgba(244,239,230,0.6)" }} data-testid="share-card-identity">
              {idBits.join(" · ")}
            </div>
            <SMLogo height={40} opacity={0.95} />
            <Wordmark size={28} />
            <div className="font-mono up" style={{ fontSize: 9, letterSpacing: "0.22em", color: "rgba(244,239,230,0.5)" }}>{t.daily}</div>

            {streak >= 2 && (
              <div className="font-mono up" data-testid="share-card-streak"
                style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--sm-blaze)",
                  padding: "5px 12px", borderRadius: 999, border: "1px solid rgba(255,176,102,0.45)",
                  background: "rgba(232,132,44,0.10)" }}>
                DAY {streak} STREAK
              </div>
            )}

            <SpoilerGrid grid={summary.grid} />

            <div className="font-score italic tnum" style={{ fontSize: 44, color: "var(--sm-chalk)", lineHeight: 1, marginTop: 2 }}>
              {summary.score}<span style={{ color: "rgba(244,239,230,0.5)", fontSize: 26 }}>/{summary.wickets}</span>
            </div>
            <div className="font-mono up" style={{ fontSize: 9.5, letterSpacing: "0.16em", color: "var(--sm-blaze)" }}>
              vs TARGET {summary.chase}
            </div>

            <p className="font-reading italic leading-snug" style={{ fontSize: 14.5, color: "var(--sm-pitch)", maxWidth: 280, marginTop: 2 }} data-testid="share-card-verdict">“{verdict}”</p>

            <div className="font-score italic up" style={{ fontSize: 14, color: "var(--sm-blaze)", marginTop: 2 }}>
              {lang === "hi" ? "Tum se better?" : "Think you'd have called it better?"}
            </div>
            <div className="font-mono up" style={{ fontSize: 9, letterSpacing: "0.14em", color: "rgba(244,239,230,0.42)" }}>{shareDomain()}</div>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button className="btn-primary flex-1 py-3.5 flex items-center justify-center gap-2" onClick={doShare} disabled={busy} data-testid="share-native-button">
            {busy ? <Loader2 size={17} className="spin" /> : <Share2 size={17} />} {t.share}
          </button>
          <button className="btn-secondary px-5 py-3.5 flex items-center justify-center" onClick={doDownload} disabled={busy} data-testid="share-download-button" aria-label="Download card">
            <Download size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}

function shortVerdict(line) {
  if (!line) return "One real cricket moment, every day.";
  const s = line.split(/[.!—]/)[0];
  return (s && s.length > 4 ? s : line).slice(0, 90);
}
// White-label (spec): NEVER surface the hosting/preview host. The share domain is
// explicitly configured (REACT_APP_SHARE_DOMAIN) and falls back to the brand domain.
// B58: env-first; falls back to current host so Emergent preview URLs work without env; then brand string as last resort.
const SHARE_DOMAIN = (process.env.REACT_APP_SHARE_DOMAIN || (typeof window !== "undefined" ? window.location.host : "superover.sportsmantra.co.in")).trim();
function shareDomain() {
  return SHARE_DOMAIN;
}
